/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useState, useMemo, useRef, type ComponentType, type ReactElement, type SVGProps } from 'react';
import { Form } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import { useSubCategory } from '@/components/navigation-menu-responsive/context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { buildUrl } from '@salesforce/storefront-next-runtime/site-context';
import { ArrowLeft, ChevronRight, CircleHelp, CreditCard, Heart, Package, Phone, Truck, User, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from '@/components/link';
import { Drawer, DrawerClose, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/buttons';
import CompanyLogo from '@/components/company-logo';
import { useAuth } from '@/providers/auth';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';
// ## SEO url changes ##
import { toCategoryUrl, rewriteLegacyCategoryUrl } from '@/lib/seo/url-helpers';
import type { AppConfig } from '@/types/config';

type Category = ShopperProducts.schemas['Category'];

type CategoryWithIcon = Category & {
    c_topLevelLinkLogo?: string;
    c_navigationBannerImage?: string;
    c_navigationBannerLink?: string;
    c_isMarketingBanner?: boolean;
};

type NavigationOverrideEntry = {
    title?: unknown;
    url?: unknown;
    banner?: unknown;
    bannerLink?: unknown;
    topLevelLinkLogo?: unknown;
    children?: unknown;
    isMarketingBanner?: unknown;
};

type NavigationOverrideCategory = Category & {
    c_navigationOverride?: string;
    c_navigationOverrideUrl?: string;
    c_alternativeUrl?: string;
    c_isHeaderOnly?: unknown;
    c_navigationBannerImage?: string;
    c_navigationBannerLink?: string;
    c_topLevelLinkLogo?: string;
    c_isMarketingBanner?: boolean;
};

type NavigationDirection = 'forward' | 'back' | null;

export interface MobileMenuDropdownProps {
    isOpen: boolean;
    categories: Category[];
    trigger: ReactElement;
    onOpenChange: (isOpen: boolean) => void;
    onClose: () => void;
}

function isNavigationOverrideEntry(value: unknown): value is NavigationOverrideEntry {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getNavigationOverrideEntries(category: Category): NavigationOverrideEntry[] {
    const navigationOverride = (category as NavigationOverrideCategory).c_navigationOverride;

    if (navigationOverride == null) {
        return [];
    }

    if (typeof navigationOverride !== 'string') {
        throw new Error('Expected c_navigationOverride to be a stringified JSON array.');
    }

    if (navigationOverride.trim() === '') {
        return [];
    }

    const parsedNavigationOverride: unknown = JSON.parse(navigationOverride);

    if (!Array.isArray(parsedNavigationOverride)) {
        throw new Error('Expected c_navigationOverride to parse to an array.');
    }

    return parsedNavigationOverride.filter(isNavigationOverrideEntry);
}

function normalizeNavigationOverrideEntry(
    entry: NavigationOverrideEntry,
    index: number,
    parentId: string
): Category | null {
    const title = typeof entry.title === 'string' ? entry.title.trim() : '';
    const url = typeof entry.url === 'string' ? entry.url.trim() : '';
    const banner = typeof entry.banner === 'string' ? entry.banner.trim() : '';
    const bannerLink = typeof entry.bannerLink === 'string' ? entry.bannerLink.trim() : '';
    const topLevelLinkLogo = typeof entry.topLevelLinkLogo === 'string' ? entry.topLevelLinkLogo.trim() : '';

    if (!title) {
        return null;
    }

    const rawChildren = Array.isArray(entry.children) ? entry.children : [];
    const categories = rawChildren
        .filter(isNavigationOverrideEntry)
        .map((childEntry, childIndex) =>
            normalizeNavigationOverrideEntry(childEntry, childIndex, `${parentId}-${index}`)
        )
        .filter((childCategory): childCategory is Category => Boolean(childCategory));

    return {
        id: `${parentId}-navigation-override-${index}`,
        name: title,
        categories,
        onlineSubCategoriesCount: categories.length,
        c_showInMenu: true,
        c_navigationOverrideUrl: url,
        ...(banner ? { c_navigationBannerImage: banner } : {}),
        ...(bannerLink ? { c_navigationBannerLink: bannerLink } : {}),
        ...(topLevelLinkLogo ? { c_topLevelLinkLogo: topLevelLinkLogo } : {}),
        ...(entry.isMarketingBanner === true ? { c_isMarketingBanner: true } : {}),
    } as NavigationOverrideCategory;
}

function getVisibleCategories(categories: Category[] | undefined): Category[] {
    return Array.isArray(categories) ? categories.filter((cat) => cat.c_showInMenu !== false) : [];
}

function getNavigationChildren(category: Category): Category[] {
    const overrideEntries = getNavigationOverrideEntries(category);

    if (overrideEntries.length > 0) {
        return overrideEntries
            .map((entry, index) => normalizeNavigationOverrideEntry(entry, index, category.id))
            .filter((overrideCategory): overrideCategory is Category => Boolean(overrideCategory));
    }

    return getVisibleCategories(category.categories);
}

function hasSubcategories(category: Category): boolean {
    const overrideEntries = getNavigationOverrideEntries(category);

    if (overrideEntries.length > 0) {
        return getNavigationChildren(category).length > 0;
    }

    // Top-level root children arrive at depth=1 without their own `categories[]`; those hydrate
    // later via the SubCategoryStore. `onlineSubCategoriesCount` IS populated at depth=1, so we
    // use it as a static "may have children" signal. The drill-in panel reads enriched data
    // via `useSubCategory(id)` once subs resolves.
    if (typeof category.onlineSubCategoriesCount === 'number' && category.onlineSubCategoriesCount > 0) {
        return true;
    }

    return Array.isArray(category.categories) && category.categories.length > 0;
}

// ## SEO url changes ## — allowOverride=true keeps BM URLs as-is; false (default) rewrites /category/ → /c/
function getCategoryUrl(config: AppConfig, category: Category, allowOverride: boolean): string {
    const cat = category as NavigationOverrideCategory;
    const stored = cat.c_navigationOverrideUrl ?? cat.c_alternativeUrl;
    if (!stored) return toCategoryUrl(config, category);
    return allowOverride ? stored : rewriteLegacyCategoryUrl(stored);
}

function isCategoryLinkable(category: Category): boolean {
    const navigationOverrideUrl = (category as NavigationOverrideCategory).c_navigationOverrideUrl;

    return (category as NavigationOverrideCategory).c_isHeaderOnly !== true && navigationOverrideUrl !== '';
}

function withoutInteractiveClassNames(className: string): string {
    return className
        .split(/\s+/)
        .filter(Boolean)
        .filter(
            (token) =>
                !token.startsWith('hover:') &&
                !token.startsWith('focus:') &&
                !token.startsWith('focus-visible:') &&
                !token.includes(':hover:') &&
                !token.includes(':focus:') &&
                !token.includes(':focus-visible:')
        )
        .join(' ');
}

type LucideIcon = ComponentType<SVGProps<SVGSVGElement>>;

type AccountLinkItem = {
    key: string;
    to: string;
    label: string;
    Icon: LucideIcon;
};

/**
 * MobileMenuDropdown - Drill-in mobile navigation drawer.
 * Tapping a category that has children pushes a new view onto the stack
 * showing that category's children. The back arrow pops the stack.
 */
export default function MobileMenuDropdown({
    isOpen,
    categories,
    trigger,
    onOpenChange,
    onClose,
}: MobileMenuDropdownProps): ReactElement | null {
    const { t } = useTranslation('header');
    const session = useAuth();
    const config = useConfig<AppConfig>();
    // ## SEO url changes ##
    const allowOverride = config?.features?.allowOverrideCategoryUrls ?? false;
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();
    const [navStack, setNavStack] = useState<Category[]>([]);
    const [navigationDirection, setNavigationDirection] = useState<NavigationDirection>(null);

    const isAuthenticated = useMemo(
        () => Boolean(session?.userType === 'registered' && session?.customerId),
        [session]
    );

    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const closeMenuLabel = t('closeMenu', 'Close menu');
    const backLabel = t('back', 'Back');
    const isRoot = navStack.length === 0;
    const currentParent = navStack[navStack.length - 1];
    // Subscribe to the SubCategoryStore for the current drill-in level. When deferred
    // subcategory data arrives, only the current view re-renders — the rest of the menu
    // stack (history of categories the user navigated through) keeps its references.
    const enrichedCurrentParent = useSubCategory(currentParent?.id ?? '') ?? currentParent;
    const rawCurrentList: Category[] = isRoot ? categories : getNavigationChildren(enrichedCurrentParent);
    const marketingBannerFromList = !isRoot
        ? (rawCurrentList.find(
              (cat) => (cat as NavigationOverrideCategory).c_isMarketingBanner === true && cat.c_showInMenu !== false
          ) as NavigationOverrideCategory | undefined)
        : undefined;
    const currentList = marketingBannerFromList
        ? rawCurrentList.filter((cat) => (cat as NavigationOverrideCategory).c_isMarketingBanner !== true)
        : rawCurrentList;
    const showPoster = navStack.length === 1;
    const posterCategory = currentParent as CategoryWithIcon | undefined;
    const posterImage = marketingBannerFromList?.c_navigationBannerImage ?? posterCategory?.c_navigationBannerImage;
    const posterHref =
        marketingBannerFromList?.c_navigationBannerLink ??
        posterCategory?.c_navigationBannerLink ??
        (currentParent ? getCategoryUrl(config, currentParent, allowOverride) : '');

    const phoneNumber = t('menu.phoneNumber', '888-800-0000');
    const phoneTelHref = `tel:${phoneNumber.replace(/[^0-9+]/g, '')}`;
    const shopByPhoneLabel = t('menu.shopByPhone', {
        phone: phoneNumber,
        defaultValue: `Shop by Phone ${phoneNumber}`,
    });
    const shopByPhoneAriaLabel = t('menu.shopByPhoneAriaLabel', {
        phone: phoneNumber,
        defaultValue: `Shop by phone, call ${phoneNumber}`,
    });

    const accountLinks: AccountLinkItem[] = isAuthenticated
        ? [
              { key: 'myAccount', to: '/account/overview', label: t('menu.myAccount', 'My Account'), Icon: User },
              { key: 'orders', to: '/account/orders', label: t('menu.orders', 'Orders'), Icon: Package },
              { key: 'wishLists', to: '/account/wishlist', label: t('menu.wishLists', 'Wish Lists'), Icon: Heart },
              {
                  key: 'orderTracking',
                  to: '/order-track',
                  label: t('menu.orderTracking', 'Order Tracking'),
                  Icon: Truck,
              },
              { key: 'financing', to: '/financing', label: t('menu.financing', 'Financing'), Icon: CreditCard },
              { key: 'help', to: '/help', label: t('menu.help', 'Help'), Icon: CircleHelp },
          ]
        : [
              { key: 'login', to: '/login', label: t('menu.login', 'Login'), Icon: User },
              {
                  key: 'orderTracking',
                  to: '/order-track',
                  label: t('menu.orderTracking', 'Order Tracking'),
                  Icon: Truck,
              },
              { key: 'financing', to: '/financing', label: t('menu.financing', 'Financing'), Icon: CreditCard },
              { key: 'help', to: '/help', label: t('menu.help', 'Help'), Icon: CircleHelp },
          ];

    const resetNavigation = () => {
        setNavigationDirection(null);
        setNavStack([]);
    };

    const handleClose = () => {
        onClose();
        resetNavigation();
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            onOpenChange(true);
            return;
        }
        handleClose();
        onOpenChange(false);
    };

    const drillIn = (category: Category) => {
        setNavigationDirection('forward');
        setNavStack((prev) => [...prev, category]);
    };

    const handleBack = () => {
        setNavigationDirection('back');
        setNavStack((prev) => prev.slice(0, -1));
    };

    const handleLinkActivate = () => {
        onClose();
        resetNavigation();
    };

    const animatedPanelKey = navStack.map((category) => category.id).join('/') || 'root';
    const animatedPanelClassNameByDirection: Record<Exclude<NavigationDirection, null>, string> = {
        forward: 'animate-in slide-in-from-right-full duration-300 ease-out motion-reduce:animate-none',
        back: 'animate-in slide-in-from-left-full duration-300 ease-out motion-reduce:animate-none',
    };
    const animatedPanelClassName = navigationDirection
        ? animatedPanelClassNameByDirection[navigationDirection]
        : undefined;

    return (
        <Drawer direction="left" open={isOpen} onOpenChange={handleOpenChange}>
            <DrawerTrigger asChild>{trigger}</DrawerTrigger>
            <DrawerContent
                className="lg:hidden bg-header-background text-header-foreground w-4/5 max-w-96"
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    closeButtonRef.current?.focus();
                }}>
                <DrawerTitle className="sr-only">{t('mobileNavigation', 'Mobile navigation menu')}</DrawerTitle>

                <DrawerClose asChild>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        className="absolute right-4 top-4 z-10 flex size-4 items-center justify-center opacity-70 hover:opacity-50 transition-opacity"
                        aria-label={closeMenuLabel}
                        title={closeMenuLabel}>
                        <X className="size-4 text-header-foreground" aria-hidden="true" />
                    </button>
                </DrawerClose>

                <nav
                    data-vaul-no-drag
                    className="flex-1 min-h-0 overflow-y-scroll touch-pan-y overscroll-contain p-6 space-y-4"
                    aria-label={t('mobileNavigation', 'Mobile navigation menu')}>
                    <div className="overflow-hidden">
                        <div key={animatedPanelKey} className={animatedPanelClassName}>
                            {isRoot && (
                                <div className="flex items-center py-2">
                                    <Link
                                        to="/"
                                        onClick={handleLinkActivate}
                                        aria-label={t('logoAlt', 'Home')}
                                        className="inline-flex items-center">
                                        <CompanyLogo
                                            title={t('logoAlt', 'Home')}
                                            className="h-5 w-auto text-header-foreground"
                                        />
                                    </Link>
                                </div>
                            )}

                            {!isRoot && currentParent && (
                                <div className="flex h-12 items-center gap-4 py-6">
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        aria-label={backLabel}
                                        className="flex size-4 shrink-0 items-center justify-center hover:opacity-50 transition-opacity">
                                        <ArrowLeft className="size-4 text-header-foreground" aria-hidden="true" />
                                    </button>
                                    <span className="flex-1 min-w-0 truncate text-xs font-semibold uppercase tracking-nav leading-4 text-header-foreground">
                                        {currentParent.name}
                                    </span>
                                </div>
                            )}

                            {showPoster && posterImage && (
                                <div className="h-[162px] w-full overflow-hidden rounded-sm bg-muted">
                                    <NavLink to={posterHref} onClick={handleLinkActivate} className="block size-full">
                                        <img
                                            src={posterImage}
                                            alt=""
                                            aria-hidden="true"
                                            className="size-full object-cover"
                                        />
                                    </NavLink>
                                </div>
                            )}

                            <ul className="flex flex-col">
                                {!isRoot && currentParent && isCategoryLinkable(currentParent) && (
                                    <li>
                                        <NavLink
                                            to={getCategoryUrl(config, currentParent, allowOverride)}
                                            onClick={handleLinkActivate}
                                            className="flex h-12 items-center py-1 hover:opacity-70 transition-opacity">
                                            <span className="flex-1 min-w-0 truncate text-sm font-normal leading-5 text-header-foreground">
                                                {t('shopAll', `Shop All ${currentParent.name}`)}
                                            </span>
                                        </NavLink>
                                    </li>
                                )}

                                {currentList.map((category) => {
                                    const categoryWithIcon = category as CategoryWithIcon;
                                    const hasChildren = hasSubcategories(category);
                                    const iconSrc = categoryWithIcon.c_topLevelLinkLogo;
                                    const drillInLabel = t('expandCategory', {
                                        category: category.name,
                                        defaultValue: `Expand ${category.name}`,
                                    });

                                    const rowContent = isRoot ? (
                                        <>
                                            <span className="block size-8 shrink-0 overflow-hidden rounded-sm bg-card">
                                                {iconSrc ? (
                                                    <img
                                                        src={iconSrc}
                                                        alt=""
                                                        aria-hidden="true"
                                                        className="block size-full object-cover"
                                                    />
                                                ) : null}
                                            </span>
                                            <span className="flex-1 min-w-0 truncate text-xs font-semibold uppercase tracking-nav leading-4 text-header-foreground">
                                                {category.name}
                                            </span>
                                            {hasChildren ? (
                                                <ChevronRight
                                                    className="size-4 shrink-0 text-header-foreground"
                                                    aria-hidden="true"
                                                />
                                            ) : null}
                                        </>
                                    ) : (
                                        <>
                                            <span className="flex-1 min-w-0 truncate text-sm font-normal leading-5 text-header-foreground">
                                                {category.name}
                                            </span>
                                            {hasChildren ? (
                                                <ChevronRight
                                                    className="size-4 shrink-0 text-header-foreground"
                                                    aria-hidden="true"
                                                />
                                            ) : null}
                                        </>
                                    );

                                    const rowClassName = isRoot
                                        ? 'flex h-12 w-full items-center gap-4 py-1 text-left hover:opacity-70 transition-opacity'
                                        : 'flex h-12 w-full items-center gap-2 py-1 text-left hover:opacity-70 transition-opacity';

                                    return (
                                        <li key={category.id}>
                                            {hasChildren ? (
                                                <button
                                                    type="button"
                                                    onClick={() => drillIn(category)}
                                                    aria-haspopup="true"
                                                    aria-label={drillInLabel}
                                                    className={rowClassName}>
                                                    {rowContent}
                                                </button>
                                            ) : isCategoryLinkable(category) ? (
                                                <NavLink
                                                    to={getCategoryUrl(config, category, allowOverride)}
                                                    onClick={handleLinkActivate}
                                                    className={rowClassName}>
                                                    {rowContent}
                                                </NavLink>
                                            ) : (
                                                <span className={withoutInteractiveClassNames(rowClassName)}>
                                                    {rowContent}
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>

                            {isRoot && (
                                <div className="flex flex-col gap-4 pt-6 pb-8">
                                    <Separator className="bg-border" />

                                    <ul className="flex flex-col">
                                        {accountLinks.map(({ key, to, label, Icon }) => (
                                            <li key={key}>
                                                <NavLink
                                                    to={to}
                                                    onClick={handleLinkActivate}
                                                    className="flex h-12 w-full items-center gap-2 py-1 text-left hover:opacity-70 transition-opacity">
                                                    <Icon
                                                        className="size-4 shrink-0 text-header-foreground"
                                                        aria-hidden="true"
                                                    />
                                                    <span className="flex-1 min-w-0 truncate text-sm font-normal leading-5 text-header-foreground">
                                                        {label}
                                                    </span>
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>

                                    <Separator className="bg-border" />

                                    <Button
                                        asChild
                                        variant="ghost"
                                        size="sm"
                                        className="w-full justify-start gap-2 px-0 normal-case tracking-normal text-sm font-normal leading-5 text-header-foreground hover:bg-transparent hover:opacity-70">
                                        <a
                                            href={phoneTelHref}
                                            aria-label={shopByPhoneAriaLabel}
                                            onClick={handleLinkActivate}>
                                            <Phone className="size-4 shrink-0" aria-hidden="true" />
                                            <span>{shopByPhoneLabel}</span>
                                        </a>
                                    </Button>

                                    {isAuthenticated && (
                                        <Form
                                            method="post"
                                            action={buildUrl({
                                                to: '/logout',
                                                urlConfig: config.url,
                                                params: { siteId: siteRef, localeId: localeRef },
                                            })}
                                            className="w-full">
                                            <Button type="submit" variant="primary" size="default" className="w-full">
                                                {t('menu.logOut', 'Log Out')}
                                            </Button>
                                        </Form>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </nav>
            </DrawerContent>
        </Drawer>
    );
}
